<template>
  <template v-if="element">
    <component
      :is="customComponent(element)"
      :id="id"
      :color="getColor(column, value)"
      :column="column"
      :icon-class="getIconClass(column, value)"
      :resource-type="resourceType"
      :mutation="mutation"
      :value="value"
      :options="options"
      :settings="settings"
      @update:data="(data) => $emit('update:data', data)"
    ></component>
  </template>
  <template v-else>
    <!--    <div class="bg-danger p-2">Component missing for "{{ column }}"!</div>-->
    <span v-if="value === null && !isEnum()" class="text-muted">(leer)</span>
    <template v-if="value !== null || isEnum()">
      <model-title
        v-if="isModelTitle()"
        :data="{ image: imageUrl }"
        :options="{ image: !!imageUrl }"
        :value="value"
      ></model-title>
      <amount v-if="isAmount()" :value="value"></amount>
      <confirmation v-if="isConfirmation()" :value="value"></confirmation>
      <money-value v-if="isMoneyValue()" :value="value"></money-value>
      <weight v-if="isWeight()" :unit="getUnit()" :value="value"></weight>
      <json-field v-if="isJson()" :value="value"></json-field>
      <belongs-to-relation-field
        v-if="isBelongsTo()"
        :show-id="$page.props.user.roles.includes('tech')"
        :value="value"
      ></belongs-to-relation-field>
      <enum
        v-if="isEnum() && !isToggleIcon()"
        :color="getColor(column, value)"
        :column="column"
        :icon-class="getIconClass(column, value)"
        :resource-type="resourceType"
        :value="value"
      ></enum>

      <toggle-icon
        v-if="isToggleIcon()"
        :id="id"
        :color="getColor(column, value)"
        :column="column"
        :icon-class="getIconClass(column, value)"
        :mutation="mutation"
        :resource-type="resourceType"
        :value="value"
        @update:data="(data) => $emit('update:data', data)"
      ></toggle-icon>
      <set-field
        v-if="isSet()"
        :classes="classes"
        :colors="colors"
        :column="column"
        :resource-type="resourceType"
        :values="value"
      ></set-field>
      <color v-if="isColor()" :value="value"></color>
      <time-field v-if="isTime()" :output-format="getTimeFormat()" :value="value"></time-field>
      <date-field v-if="isDate()" :options="options" :output-format="getDateFormat()" :value="value"></date-field>
      <date-time-field v-if="isDateTime()" :options="options" :value="value"></date-time-field>
      <hidden-field v-if="isHidden()"></hidden-field>
      <tags v-if="isTags()" :tags="value"></tags>
      <image-element v-if="isImage()" :value="value"></image-element>
      <crementable
        v-if="isCrementable()"
        :id="id"
        :column="column"
        :resource-type="resourceType"
        :value="value"
        @update:data="(data) => $emit('update:data', data)"
      ></crementable>
      <expandable-container v-if="isExpandable()" :height="'6rem'">
        <span v-html="value"></span>
      </expandable-container>
      <span v-else-if="isPlain()" v-html="value"></span>
      <i
        v-if="isCopiable()"
        class="bi bi-files fs-5 ms-1"
        style="cursor: pointer"
        title="In die Zwischenablage kopieren"
        @click="copy(value, $emit)"
      ></i>
    </template>
  </template>
</template>

<script>
import Amount from '@kanrisha/components/Elements/Amount/BaseAmount';
import Color from '@kanrisha/components/Elements/Display/Color';
import Confirmation from '@kanrisha/components/Elements/Display/Confirmation';
import Crementable from '@kanrisha/components/Elements/Display/Crementable';
import DateField from '@kanrisha/components/Elements/Display/DateField';
import DateTimeField from '@kanrisha/components/Elements/Display/DateTimeField';
import Enum from '@kanrisha/components/Elements/Display/Enum';
import JsonField from '@kanrisha/components/Presentation/JsonField';
import ModelTitle from '@kanrisha/components/Presentation/ModelTitle';
import MoneyValue from '@kanrisha/components/Elements/Amount/MoneyValue';
import ImageElement from '@kanrisha/components/Elements/Display/ImageElement';
import HiddenField from '@kanrisha/components/Presentation/HiddenField';
import BelongsToRelationField from '@kanrisha/components/Presentation/BelongsToRelationField';
import SetField from '@kanrisha/components/Presentation/SetField';
import Tags from '@kanrisha/components/Presentation/Tags';
import TimeField from '@kanrisha/components/Elements/Display/TimeField';
import ToggleIcon from '@kanrisha/components/Elements/Display/ToggleIcon';
import useClipboard from 'vue-clipboard3';
import Weight from '@kanrisha/components/Elements/Amount/Weight';
import ExpandableContainer from '@kanrisha/components/Elements/Container/ExpandableContainer';
import useCustomComponent from '@kanrisha/composables/useCustomComponent';

export default {
  components: {
    Weight,
    Amount,
    Color,
    Confirmation,
    Crementable,
    DateField,
    DateTimeField,
    Enum,
    ExpandableContainer,
    JsonField,
    ModelTitle,
    MoneyValue,
    ImageElement,
    HiddenField,
    BelongsToRelationField,
    SetField,
    Tags,
    TimeField,
    ToggleIcon,
  },
  props: {
    value: { type: [Number, String, Boolean, Object], default: null },
    id: { type: Number, default: null },
    classes: { type: Object, default: () => {} },
    colors: { type: Object, default: () => {} },
    column: { type: String, default: '' },
    columnType: { type: String, default: '' },
    resourceType: { type: String, default: '' },
    element: { type: String, default: () => null },
    meta: { type: Object, default: () => {} },
    options: { type: Object, default: () => {} }, // deprecated
    settings: { type: Object, default: () => {} },
    mutation: { type: String, default: null },
    showId: { type: Boolean, default: true },
    imageUrl: { type: String, default: null },
  },
  emits: ['addToast', 'update:data'],
  setup(props) {
    const { customComponent } = useCustomComponent();

    const getTimeFormat = () => {
      if (props.mutation === null || props.mutation === 'time' || props.mutation.substring(0, 4) !== 'time') {
        return null;
      }

      return props.mutation.substring(5, props.mutation.length - 1);
    };
    const getDateFormat = () => {
      if (props.mutation === null || props.mutation === 'date' || props.mutation.substring(0, 4) !== 'date') {
        return null;
      }

      return props.mutation.substring(5, props.mutation.length - 1);
    };

    const getUnit = () => {
      if (props.mutation.split('.').shift() === 'weight') {
        return props.mutation.split('.').pop();
      }

      return false;
    };

    const isAmount = () => {
      // if (props.mutation === 'weight.gram') {
      //   return true;
      // }

      return false;
    };

    const isWeight = () => {
      if (props.mutation?.split('.').shift() === 'weight') {
        return true;
      }

      return false;
    };

    const isColor = () => {
      return props.mutation && props.mutation === 'color';
    };

    const isMoneyValue = () => {
      return props.mutation && props.mutation.split('.').shift() === 'currency';
    };

    const isDate = () => {
      return props.columnType === 'date';
    };

    const isDateTime = () => {
      return (
        (props.columnType === 'datetime' || props.columnType === 'timestamp') &&
        (!props.mutation || props.mutation.substring(0, 4) !== 'time')
      );
    };

    const isEnum = () => {
      return props.columnType === 'enum' || props.columnType === 'select';
    };

    const isSet = () => {
      return props.columnType === 'set' || props.columnType === 'select-multiple';
    };

    const isJson = () => {
      return props.columnType === 'json' || props.columnType === 'array';
    };

    const isBelongsTo = () => {
      return props.columnType === 'relation' || props.columnType === 'belongs-to';
    };

    const isTime = () => {
      return props.columnType === 'datetime' && props.mutation && props.mutation.substring(0, 4) === 'time';
    };

    const isHidden = () => {
      return props.columnType === 'hidden' || props.mutation === 'password';
    };

    const isMutated = () => {
      if (!props.mutation || props.mutation == props.columnType) {
        return false;
      }

      let fieldType = props.mutation.split('.').shift();
      if (['varchar'].includes(fieldType)) {
        return false;
      }

      return true;
    };

    const isModelTitle = () => {
      return props.column === 'model-title';
    };

    const isImage = () => {
      return props.columnType === 'image';
    };

    const isPlain = () => {
      return (
        !isAmount() &&
        !isMutated() &&
        !isEnum() &&
        !isJson() &&
        !isBelongsTo() &&
        !isHidden() &&
        !isDateTime() &&
        !isDate() &&
        !isTags() &&
        !isTime() &&
        !isSet() &&
        !isCrementable() &&
        !isMoneyValue() &&
        !isWeight() &&
        !isImage() &&
        !isModelTitle()
      );
    };

    const isCopiable = () => {
      if (props.mutation) {
        let mutations = props.mutation.split('.');
        for (let key in mutations) {
          if (mutations[key] === 'copiable') {
            return true;
          }
        }
      }

      return false;
    };

    const isExpandable = () => {
      if (props.mutation) {
        let mutations = props.mutation.split('.');
        for (let key in mutations) {
          if (mutations[key] === 'expandable') {
            return true;
          }
        }
      }

      return false;
    };

    const isConfirmation = () => {
      return props.mutation === 'confirmation';
    };

    const isTags = () => {
      return props.columnType === 'tags';
    };

    const isToggleIcon = () => {
      return props.mutation && props.mutation.includes('icon.toggle');
    };

    const isCrementable = () => {
      return props.columnType === 'crementable';
    };

    const { toClipboard } = useClipboard();

    const copy = async (value, emit) => {
      try {
        await toClipboard(value);
        emit('addToast', {
          message: 'Gutschein in die Zwischenablage kopiert',
          type: 'success',
        });
      } catch (e) {
        console.error(e);
      }
    };

    const getColor = (column, option) => {
      if (!props.meta?.decoration.options[column]) {
        return null;
      }

      return props.meta.decoration.options[column][option]?.color;
    };

    const getIconClass = (column, option) => {
      if (!props.meta?.decoration.options[column]) {
        return null;
      }

      return props.meta.decoration.options[column][option ?? '']?.iconClass;
    };

    return {
      copy,
      customComponent,
      getColor,
      getDateFormat,
      getIconClass,
      getTimeFormat,
      getUnit,
      isAmount,
      isBelongsTo,
      isColor,
      isConfirmation,
      isCopiable,
      isCrementable,
      isDate,
      isDateTime,
      isEnum,
      isExpandable,
      isImage,
      isHidden,
      isModelTitle,
      isJson,
      isMoneyValue,
      isMutated,
      isPlain,
      isSet,
      isTags,
      isTime,
      isToggleIcon,
      isWeight,
    };
  },
};
</script>
